import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../../bodygraph/hd-planet-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../../bodygraph/planet-grid/hd-planet-display-config';
import { HdJustNowTransitPinV1 } from '../../bodygraph/hd-transit-chart.models';
import {
  MOON_TRANSIT_CARD_ANCHOR_RADIUS,
  MOON_TRANSIT_MANDALA_GEOMETRY,
  MOON_TRANSIT_WHEEL_EDGE_RADIUS,
  MandalaGeometryConfig,
} from '../mandala.constants';
import { polarToCartesian } from '../mandala-geometry.utils';
import { buildMandalaGateSegments } from '../mandala-layout';
import { HD_MOON_TRANSIT_TYPE_COLORS } from '../moon-transit/hd-moon-transit-card.models';
import {
  MoonTransitRingLayout,
  MoonTransitStackLayoutItem,
  moonTransitAnchorFractions,
  moonTransitRectAtRadius,
  moonTransitStackAnchorRadius,
  moonTransitTearOutPlacement,
  resolveMoonTransitStackAngles,
} from '../moon-transit/hd-moon-transit-card-layout';
import { HD_JUST_NOW_TRANSIT_TEXT_COLOR, HdJustNowTransitFormation } from './hd-just-now-transit';
import { HdJustNowTransitFormationClickEvent } from './hd-just-now-transit-view.models';
import { HdActivePaletteService } from '../../bodygraph/hd-active-palette.service';

interface JustNowPlanetVm {
  readonly key: string;
  readonly glyph: string;
  readonly title: string;
}

interface JustNowCardVm {
  readonly formation: HdJustNowTransitFormation;
  readonly key: string;
  readonly title: string;
  readonly keynote: string;
  readonly typeColor: string;
  readonly planets: readonly JustNowPlanetVm[];
  readonly centerIcons: readonly { readonly html: SafeHtml; readonly label: string }[];
  readonly showBridge: boolean;
}

interface JustNowStackVm {
  /**
   * Track-key voor de template: auto-stapel `gate:<anchorGate>` (ongepinde
   * formaties van één ankerpoort samen), singleton `card:<channelKey>` (één
   * gepinde formatie los). De structuur wijzigt dus enkel bij
   * pin-commit/-remove — nooit tijdens een sleep, zodat het capture-dragende
   * kaart-element blijft bestaan (zelfde patroon als de maan-transit-laag).
   */
  readonly key: string;
  readonly gate: number;
  readonly midAngle: number;
  readonly cards: readonly JustNowCardVm[];
  /** Gepinde singleton-formatie (eigen versleepbare card) of null voor een auto-stapel. */
  readonly singleton: HdJustNowTransitFormation | null;
}

interface JustNowStackPlacement {
  readonly angle: number;
  readonly radiusUnits: number;
}

/** Lopende sleep van één formatie-card — zelfde mechaniek als de maan-transit-laag. */
interface JustNowDragState {
  readonly channelKey: string;
  readonly pointerId: number;
  readonly startPointerPx: { readonly x: number; readonly y: number };
  readonly hostLeft: number;
  readonly hostTop: number;
  readonly unitsToPx: number;
  readonly centerPx: { readonly x: number; readonly y: number };
  readonly originalCardRectPx: { readonly left: number; readonly top: number; readonly width: number; readonly height: number };
  readonly dragDeltaPx: { readonly x: number; readonly y: number };
  readonly snapActive: boolean;
  readonly currentPlacement: JustNowStackPlacement;
  readonly movedPx: number;
}

const WHEEL_CLEARANCE_GAP_UNITS = 2;

/** Schermverplaatsing (px) waaronder een pointerup als klik telt, niet als sleep. */
const JUST_NOW_CARD_DRAG_THRESHOLD_PX = 6;

/** Radiale magneet-tolerantie (viewBox-units): binnen dit bereik snapt de straal op de default-ring. */
const JUST_NOW_CARD_RADIAL_SNAP_UNITS = 8;

@Component({
  selector: 'hd-just-now-transit-card-layer',
  imports: [SharedModule],
  templateUrl: './hd-just-now-transit-card-layer.component.html',
  styleUrl: './hd-just-now-transit-card-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--hd-just-now-transit-text-color]': 'transitTextColor',
    '[class.move-active]': 'moveToolActive()',
    '[class.dragging]': 'dragState() !== null',
  },
})
export class HdJustNowTransitCardLayerComponent {
  readonly formations = input.required<readonly HdJustNowTransitFormation[]>();
  readonly geometry = input<MandalaGeometryConfig>(MOON_TRANSIT_MANDALA_GEOMETRY);
  readonly highlightedChannelKeys = input<ReadonlySet<string>>(new Set<string>());
  readonly formationHovered = output<HdJustNowTransitFormation | null>();
  readonly formationClicked = output<HdJustNowTransitFormationClickEvent>();

  /**
   * Handmatig gepinde kaarten (polair, per channelKey): winnen van de
   * collision-oplossing/default en tellen als vaste obstakels mee. De page
   * bindt dit uit de chart-preset (`justNowTransitLayout`, spec notable
   * moments §4.1).
   */
  readonly pins = input<readonly HdJustNowTransitPinV1[]>([]);

  /** Verplaats-tool: alléén dan zijn de kaarten sleepbaar (zoals de maan-laag). */
  readonly moveToolActive = input(false);

  /** Sleep afgerond → pin (ringhoek + straal in viewBox-units, per formatie). */
  readonly pinCommitted = output<HdJustNowTransitPinV1>();
  /** Dubbelklik op een gepinde card → unpin (per formatie). */
  readonly pinRemoved = output<{ channelKey: string }>();

  protected readonly transitTextColor = HD_JUST_NOW_TRANSIT_TEXT_COLOR;

  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly centerColors = inject(HdActivePaletteService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly resolvedPlacements = signal<ReadonlyMap<number, JustNowStackPlacement>>(new Map());

  /** Lopende sleep (of `null`); de transiente plaatsing wint in de kaart-preview. */
  protected readonly dragState = signal<JustNowDragState | null>(null);

  /** Pin per formatie (channelKey) — snelle lookup voor plaatsing én obstakel-opbouw. */
  private readonly pinsByChannelKey = computed<ReadonlyMap<string, HdJustNowTransitPinV1>>(
    () => new Map(this.pins().map(pin => [pin.channelKey, pin])),
  );

  constructor() {
    effect(() => {
      // Zelfde tracking-afspraken als de maan-laag: pins() en dragState bewust
      // NIET getrackt — pin-mutaties bevriezen de auto-posities (een expliciete
      // herberekening loopt via recomputeAutoPlacements), een sleep is een pure
      // transform-preview. Wél getrackt: alles wat kaart-maten wijzigt.
      this.formations();
      this.geometry();
      const lang = this.contentLang.effectiveLang();
      this.gateInfoService.channelTranslationsIn(lang);
      untracked(() => requestAnimationFrame(() => this.measureAndResolveCollisions()));
    });

    // Tool uit → een lopende sleep annuleren zonder commit.
    effect(() => {
      if (!this.moveToolActive()) {
        untracked(() => this.dragState.set(null));
      }
    });
  }

  /**
   * Render-structuur (hangt aan formaties + pins, NIET aan de sleep): per
   * ankerpoort één auto-stapel met de óngepinde formaties, plus één losse
   * singleton-stapel per gepinde formatie.
   */
  protected readonly renderStacks = computed<readonly JustNowStackVm[]>(() => {
    const geometry = this.geometry();
    const lang = this.contentLang.effectiveLang();
    const translations = this.gateInfoService.channelTranslationsIn(lang);
    const segments = new Map(buildMandalaGateSegments(geometry).map(segment => [segment.gate, segment]));
    const pinnedKeys = this.pinsByChannelKey();
    const byGate = new Map<number, HdJustNowTransitFormation[]>();
    this.formations().forEach(formation => {
      const group = byGate.get(formation.anchorGate);
      if (group) group.push(formation);
      else byGate.set(formation.anchorGate, [formation]);
    });

    const stacks: JustNowStackVm[] = [];
    for (const [gate, group] of byGate) {
      const segment = segments.get(gate);
      if (!segment) continue;
      const autoGroup = group.filter(formation => !pinnedKeys.has(formation.channelKey));
      if (autoGroup.length > 0) {
        stacks.push({
          key: `gate:${gate}`,
          gate,
          midAngle: segment.midAngle,
          cards: autoGroup.map(formation => this.buildCard(formation, translations, lang)),
          singleton: null,
        });
      }
      for (const formation of group) {
        if (pinnedKeys.has(formation.channelKey)) {
          stacks.push({
            key: `card:${formation.channelKey}`,
            gate,
            midAngle: segment.midAngle,
            cards: [this.buildCard(formation, translations, lang)],
            singleton: formation,
          });
        }
      }
    }
    return stacks;
  });

  protected onCardClick(formation: HdJustNowTransitFormation, event: MouseEvent): void {
    // Onder de verplaats-tool alleen de dialog onderdrukken; hover blijft live.
    if (this.moveToolActive()) {
      return;
    }
    this.formationClicked.emit({ formation, origin: { x: event.clientX, y: event.clientY } });
  }

  /** Gepinde singleton → amber pin-dot (alleen zichtbaar met actieve tool). */
  protected isPinned(stack: JustNowStackVm): boolean {
    return stack.singleton != null && this.pinsByChannelKey().has(stack.singleton.channelKey);
  }

  /** De kaart die nu gesleept wordt (transform-preview) — of niet. */
  protected isDragging(card: JustNowCardVm): boolean {
    return this.dragState()?.channelKey === card.key;
  }

  /** De stapel bevat de gesleepte kaart → tijdelijk boven de overige stapels (z-index). */
  protected stackHasDrag(stack: JustNowStackVm): boolean {
    const key = this.dragState()?.channelKey;
    return key != null && stack.cards.some(card => card.key === key);
  }

  /** Sleep-preview: pure translate; in de magneet-zone de gesnapte reconstructie (maan-mechaniek). */
  protected cardTransform(card: JustNowCardVm): string | null {
    const drag = this.dragState();
    if (!drag || drag.channelKey !== card.key) {
      return null;
    }
    if (drag.snapActive) {
      const { angle, radiusUnits } = drag.currentPlacement;
      const anchor = polarToCartesian(drag.centerPx.x, drag.centerPx.y, radiusUnits * drag.unitsToPx, angle);
      const { fx, fy } = moonTransitAnchorFractions(angle);
      const dx = anchor.x - fx * drag.originalCardRectPx.width - drag.originalCardRectPx.left;
      const dy = anchor.y - fy * drag.originalCardRectPx.height - drag.originalCardRectPx.top;
      return `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`;
    }
    return `translate(${drag.dragDeltaPx.x.toFixed(2)}px, ${drag.dragDeltaPx.y.toFixed(2)}px)`;
  }

  protected onCardPointerDown(card: JustNowCardVm, event: PointerEvent): void {
    if (!this.moveToolActive()) {
      return;
    }
    event.preventDefault();
    const cardEl = (event.target as HTMLElement).closest('.hd-just-now-transit-card') as HTMLElement | null;
    if (!cardEl) {
      return;
    }
    try {
      cardEl.setPointerCapture(event.pointerId);
    } catch {
      // negeren: geen capture, sleep gaat door
    }
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const geometry = this.geometry();
    const unitsToPx = hostRect.width / geometry.viewBoxSize;
    const centerPx = { x: geometry.center.x * unitsToPx, y: geometry.center.y * unitsToPx };
    const cardRect = cardEl.getBoundingClientRect();
    const originalCardRectPx = {
      left: cardRect.left - hostRect.left,
      top: cardRect.top - hostRect.top,
      width: cardRect.width,
      height: cardRect.height,
    };
    const { placement } = moonTransitTearOutPlacement(originalCardRectPx, centerPx, unitsToPx);
    this.dragState.set({
      channelKey: card.key,
      pointerId: event.pointerId,
      startPointerPx: { x: event.clientX - hostRect.left, y: event.clientY - hostRect.top },
      hostLeft: hostRect.left,
      hostTop: hostRect.top,
      unitsToPx,
      centerPx,
      originalCardRectPx,
      dragDeltaPx: { x: 0, y: 0 },
      snapActive: false,
      currentPlacement: placement,
      movedPx: 0,
    });
  }

  protected onLayerPointerMove(event: PointerEvent): void {
    const drag = this.dragState();
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const dx = event.clientX - drag.hostLeft - drag.startPointerPx.x;
    const dy = event.clientY - drag.hostTop - drag.startPointerPx.y;
    const previewRect = {
      left: drag.originalCardRectPx.left + dx,
      top: drag.originalCardRectPx.top + dy,
      width: drag.originalCardRectPx.width,
      height: drag.originalCardRectPx.height,
    };
    const { placement } = moonTransitTearOutPlacement(previewRect, drag.centerPx, drag.unitsToPx);
    const snapActive = Math.abs(placement.radiusUnits - MOON_TRANSIT_CARD_ANCHOR_RADIUS) <= JUST_NOW_CARD_RADIAL_SNAP_UNITS;
    const radiusUnits = snapActive ? MOON_TRANSIT_CARD_ANCHOR_RADIUS : placement.radiusUnits;
    this.dragState.set({
      ...drag,
      dragDeltaPx: { x: dx, y: dy },
      currentPlacement: { angle: placement.angle, radiusUnits },
      snapActive,
      movedPx: Math.max(drag.movedPx, Math.hypot(dx, dy)),
    });
  }

  protected onLayerPointerUp(event: PointerEvent): void {
    const drag = this.dragState();
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    this.dragState.set(null);
    if (drag.movedPx < JUST_NOW_CARD_DRAG_THRESHOLD_PX) {
      return; // klik ≠ pin
    }
    // Compensatie-anker vóór de commit (zelfde motivatie als de maan-laag): de
    // reststapel blijft exact staan wanneer de gesleepte kaart eruit vertrekt.
    const stacks = this.renderStacks();
    const sourceIndex = stacks.findIndex(
      stack => stack.singleton == null && stack.cards.some(card => card.key === drag.channelKey),
    );
    if (sourceIndex >= 0 && stacks[sourceIndex].cards.length >= 2) {
      this.compensateAutoStackAnchor(
        stacks[sourceIndex].gate,
        this.stackCardNodes(sourceIndex, drag.channelKey),
        drag.centerPx, drag.unitsToPx, drag.hostLeft, drag.hostTop,
      );
    }
    this.pinCommitted.emit({
      channelKey: drag.channelKey,
      angleDeg: drag.currentPlacement.angle,
      radiusUnits: drag.currentPlacement.radiusUnits,
    });
  }

  protected onCardDblClick(card: JustNowCardVm): void {
    if (!this.moveToolActive() || !this.pinsByChannelKey().has(card.key)) {
      return;
    }
    const gate = card.formation.anchorGate;
    const autoIndex = this.renderStacks().findIndex(
      stack => stack.singleton == null && stack.gate === gate,
    );
    if (autoIndex >= 0) {
      const hostRect = this.host.nativeElement.getBoundingClientRect();
      const geometry = this.geometry();
      const unitsToPx = hostRect.width / geometry.viewBoxSize;
      const centerPx = { x: geometry.center.x * unitsToPx, y: geometry.center.y * unitsToPx };
      this.compensateAutoStackAnchor(
        gate, this.stackCardNodes(autoIndex, null),
        centerPx, unitsToPx, hostRect.left, hostRect.top,
      );
    }
    this.pinRemoved.emit({ channelKey: card.key });
  }

  /** Expliciete herberekening van de automatische kaartposities (contextmenu). */
  recomputeAutoPlacements(): void {
    requestAnimationFrame(() => this.measureAndResolveCollisions());
  }

  protected stackLeftPercent(stack: JustNowStackVm): number {
    return this.anchorPercent(stack).x;
  }

  protected stackTopPercent(stack: JustNowStackVm): number {
    return this.anchorPercent(stack).y;
  }

  protected stackTransform(stack: JustNowStackVm): string {
    const { fx, fy } = moonTransitAnchorFractions(this.effectivePlacement(stack).angle);
    return `translate(${(-fx * 100).toFixed(2)}%, ${(-fy * 100).toFixed(2)}%)`;
  }

  private anchorPercent(stack: JustNowStackVm): { x: number; y: number } {
    const geometry = this.geometry();
    const placement = this.effectivePlacement(stack);
    const point = polarToCartesian(
      geometry.center.x,
      geometry.center.y,
      placement.radiusUnits,
      placement.angle,
    );
    return {
      x: point.x / geometry.viewBoxSize * 100,
      y: point.y / geometry.viewBoxSize * 100,
    };
  }

  /** Gepinde singleton wint met zijn pin; auto-stapels volgen de collision-oplossing/default. */
  private effectivePlacement(stack: JustNowStackVm): JustNowStackPlacement {
    if (stack.singleton) {
      const pin = this.pinsByChannelKey().get(stack.singleton.channelKey);
      if (pin) {
        return { angle: pin.angleDeg, radiusUnits: pin.radiusUnits };
      }
    }
    return this.resolvedPlacements().get(stack.gate)
      ?? { angle: stack.midAngle, radiusUnits: MOON_TRANSIT_CARD_ANCHOR_RADIUS };
  }

  private ringLayout(hostRect: DOMRect): MoonTransitRingLayout {
    const geometry = this.geometry();
    const unitsToPx = hostRect.width / geometry.viewBoxSize;
    return {
      centerX: geometry.center.x * unitsToPx,
      centerY: geometry.center.y * unitsToPx,
      anchorRadius: MOON_TRANSIT_CARD_ANCHOR_RADIUS * unitsToPx,
      wheelClearanceRadius: (MOON_TRANSIT_WHEEL_EDGE_RADIUS + WHEEL_CLEARANCE_GAP_UNITS) * unitsToPx,
    };
  }

  private measureAndResolveCollisions(): void {
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    if (hostRect.width < 50 || hostRect.height < 50) {
      return;
    }
    const geometry = this.geometry();
    const unitsToPx = hostRect.width / geometry.viewBoxSize;
    const ring = this.ringLayout(hostRect);
    const stacks = this.renderStacks();
    const nodes = this.host.nativeElement.querySelectorAll<HTMLElement>('.hd-just-now-transit-stack');
    const pinsByChannelKey = this.pinsByChannelKey();
    const items: MoonTransitStackLayoutItem[] = [];
    nodes.forEach((node, index) => {
      const stack = stacks[index];
      if (stack == null) {
        return;
      }
      const rect = node.getBoundingClientRect();
      const pin = stack.singleton ? pinsByChannelKey.get(stack.singleton.channelKey) : undefined;
      if (pin) {
        // Gepinde singleton = vast obstakel op zijn eigen straal.
        items.push({
          gate: stack.gate,
          desiredAngle: pin.angleDeg,
          width: rect.width,
          height: rect.height,
          fixed: true,
          rect: moonTransitRectAtRadius(rect.width, rect.height, pin.angleDeg, ring, pin.radiusUnits * unitsToPx),
        });
        return;
      }
      items.push({
        gate: stack.gate,
        desiredAngle: stack.midAngle,
        width: rect.width,
        height: rect.height,
      });
    });
    const angles = resolveMoonTransitStackAngles(items, ring);
    const placements = new Map<number, JustNowStackPlacement>();
    items.forEach(item => {
      if (item.fixed) {
        return;
      }
      const angle = angles.get(item.gate) ?? item.desiredAngle;
      const radiusPx = moonTransitStackAnchorRadius(item.width, item.height, angle, ring);
      placements.set(item.gate, { angle, radiusUnits: radiusPx / unitsToPx });
    });
    if (placements.size > 0) {
      this.resolvedPlacements.set(placements);
    }
  }

  /** Compensatie-anker (union-rect van de opgegeven kaarten) — zelfde mechaniek als de maan-laag. */
  private compensateAutoStackAnchor(
    gate: number,
    cardNodes: readonly HTMLElement[],
    centerPx: { readonly x: number; readonly y: number },
    unitsToPx: number,
    hostLeft: number,
    hostTop: number,
  ): void {
    if (cardNodes.length === 0 || unitsToPx <= 0) {
      return;
    }
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    for (const node of cardNodes) {
      const rect = node.getBoundingClientRect();
      left = Math.min(left, rect.left - hostLeft);
      top = Math.min(top, rect.top - hostTop);
      right = Math.max(right, rect.right - hostLeft);
      bottom = Math.max(bottom, rect.bottom - hostTop);
    }
    const union = { left, top, width: right - left, height: bottom - top };
    const { placement } = moonTransitTearOutPlacement(union, centerPx, unitsToPx);
    const next = new Map(this.resolvedPlacements());
    next.set(gate, placement);
    this.resolvedPlacements.set(next);
  }

  /** De kaart-DOM-nodes van de stapel op `stackIndex`, optioneel zonder `excludeKey`. */
  private stackCardNodes(stackIndex: number, excludeKey: string | null): HTMLElement[] {
    const stack = this.renderStacks()[stackIndex];
    const stackNode = this.host.nativeElement
      .querySelectorAll<HTMLElement>('.hd-just-now-transit-stack')[stackIndex];
    if (stack == null || stackNode == null) {
      return [];
    }
    const cardNodes = Array.from(stackNode.querySelectorAll<HTMLElement>('.hd-just-now-transit-card'));
    const result: HTMLElement[] = [];
    stack.cards.forEach((card, index) => {
      const node = cardNodes[index];
      if (node != null && card.key !== excludeKey) {
        result.push(node);
      }
    });
    return result;
  }

  private buildCard(
    formation: HdJustNowTransitFormation,
    translations: ReturnType<HdGateInfoService['channelTranslationsIn']>,
    lang: string,
  ): JustNowCardVm {
    const translation = translations[formation.channelKey];
    return {
      formation,
      key: formation.channelKey,
      title: `${formation.channelKey} ${translation?.name ?? ''}`.trim(),
      keynote: translation?.shortDescription ?? '',
      typeColor: HD_MOON_TRANSIT_TYPE_COLORS[formation.manifestationType],
      planets: formation.transitActivations.map(activation => this.buildPlanetVm(activation, lang)),
      centerIcons: formation.newlyDefinedCenters.map(center => ({
        html: this.centerColors.iconHtml(center),
        label: this.gateInfoService.centerLabelIn(center, lang),
      })),
      showBridge: formation.bridgesSplit,
    };
  }

  private buildPlanetVm(
    activation: HdJustNowTransitFormation['transitActivations'][number],
    lang: string,
  ): JustNowPlanetVm {
    const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
    const name = config ? this.planetInfoService.planetInfoIn(config.key, lang)?.name ?? activation.planet : activation.planet;
    return {
      key: activation.id,
      glyph: config?.astronomiconChar ?? '',
      title: `${name} · ${activation.gate}.${activation.line} · ${this.t('humanDesign.chart.justNowTransit.transitRowLabel')}`,
    };
  }

  protected t(key: string): string {
    return this.transloco.translate(key, undefined, this.contentLang.effectiveLang());
  }
}
